import type { FundDetail } from "@/data/fund-detail-types";
import { useLiveHoldings } from "@/hooks/use-etf-live-holdings";
import {
  EMPTY,
  formatAum,
  formatChangePct,
  formatPrice,
  useFundLiveData,
} from "@/hooks/use-fund-live-data";
import { InfoTooltip as InfoIcon } from "./InfoTooltip";

/**
 * Stat-ticker strip rendered below the hero / above the section nav. Ported
 * from the dane etf-website `components/fund-research.tsx` (`FundStatTicker`);
 * the unused `FundResearch` / `ReasonCard` exports are omitted because the
 * fund-detail template never renders them.
 */
function formatBaseCurrencyForStat(value: string): string {
  if (value === "—" || value === "" || value === "USD") return "U.S. Dollar";
  return value;
}

// Maps a stat label to the DDB Fund leaf path it reflects, so the admin preview
// can box the precise stat that changed. Live-only stats (NAV / AUM / Premium)
// have no config path and are left unmarked — the section box still covers them.
const STAT_FIELD_PATHS: Record<string, string> = {
  "Expense Ratio": "expenseRatio",
  "30-Day SEC Yield": "sec30DayYield",
  "Effective Duration": "effectiveDuration",
  "Yield to Maturity": "ytm",
  "Base Currency": "baseCurrency",
  Distribution: "distributionFrequency",
};

export function FundStatTicker({ fund }: { fund: FundDetail }) {
  const live = useFundLiveData(fund.ticker, fund) ?? EMPTY;
  const liveHoldings = useLiveHoldings(fund);
  const fact = (label: string) => fund.fundFacts.find((f) => f.label === label)?.value ?? "—";

  const liveAum =
    liveHoldings.isLive && liveHoldings.aum != null && liveHoldings.aum > 0
      ? formatAum(liveHoldings.aum)
      : live.aum != null
        ? formatAum(live.aum)
        : null;

  const navValue = live.nav != null ? formatPrice(live.nav) : "TBD";
  const aumValue = liveAum ?? "—";
  const premiumValue =
    live.premiumDiscountPct != null ? formatChangePct(live.premiumDiscountPct) : "TBD";
  const premiumIsPositive = (live.premiumDiscountPct ?? 0) >= 0;

  // Prefer the LIVE daily_nav date over the static config `aumAsOf` (which is a
  // hand-entered, frequently-stale display string). Fall back to config only
  // when there's no live snapshot yet (pre-ingestion funds).
  const navSnapshotIso = live.navAsOfDate ?? fund.navAsOf;
  const navAsOf = formatAsOfDate(navSnapshotIso);
  const aumAsOfRaw = navSnapshotIso;
  const aumAsOf = formatAsOfDate(aumAsOfRaw);

  type StatRow = {
    label: string;
    value: string;
    color?: string;
    asOf?: string | null;
    tooltip?: string;
  };
  const stats: StatRow[] =
    fund.type === "fixed"
      ? [
          { label: "Net Asset Value", value: navValue, asOf: navAsOf },
          {
            label: "30-Day SEC Yield",
            value: fact("30-Day SEC Yield"),
            tooltip:
              "The 30-Day SEC Yield is a standardized measure of the fund's income over the past 30 days, used to compare yields across funds.",
          },
          {
            label: "Effective Duration",
            value: fact("Effective Duration"),
            tooltip:
              "Effective duration is a measure of how sensitive the fund's price is to changes in interest rates. A duration of 2 years means the price would be expected to move about 2% for every 1% change in rates.",
          },
          {
            label: "Yield to Maturity",
            value: fact("Yield to Maturity"),
            tooltip:
              "Yield to maturity is the total yearly return expected if the fund's bonds are held until they mature and all scheduled payments are made.",
          },
          { label: "Expense Ratio", value: fund.expenseRatio },
          { label: "Distribution", value: fact("Distribution Frequency") },
        ]
      : [
          { label: "Net Asset Value", value: navValue, asOf: navAsOf },
          { label: "Expense Ratio", value: fund.expenseRatio },
          { label: "Assets Under Management", value: aumValue, asOf: aumAsOf },
          {
            label: "Base Currency",
            value: formatBaseCurrencyForStat(fund.baseCurrency ?? "—"),
          },
          {
            label: "Premium / Discount",
            value: premiumValue,
            color: premiumIsPositive ? "var(--etf-green)" : "var(--etf-red)",
          },
          {
            label: "Distribution",
            value:
              (fund.distributionFrequency ?? "—") === "—"
                ? "Annual"
                : (fund.distributionFrequency ?? "Annual"),
          },
        ];

  return (
    <div
      data-fund-section="expenseRatio sec30DayYield distributionFrequency baseCurrency effectiveDuration ytm"
      className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] max-md:grid-cols-[repeat(auto-fit,minmax(140px,1fr))] max-[480px]:grid-cols-2 gap-px border-b border-[var(--etf-border)]"
    >
      {stats.map((s) => (
        <div
          key={s.label}
          data-fund-field={STAT_FIELD_PATHS[s.label]}
          className="flex flex-col gap-2 px-7 py-5 max-md:px-4 max-md:py-[14px] max-[480px]:p-3 bg-[linear-gradient(to_bottom,transparent_0%,var(--etf-black2)_55%)] hover:bg-[linear-gradient(to_bottom,transparent_0%,var(--etf-black3)_55%)] transition-[background] duration-150"
        >
          <div className="font-[family-name:var(--font-instrument)] text-[10px] tracking-[0.16em] uppercase text-[var(--etf-white3)] leading-[1.4] grow">
            <span className="inline-flex items-center gap-0.5">
              {s.label}
              {s.tooltip && <InfoIcon tip={s.tooltip} direction="center" />}
            </span>
            {s.asOf && (
              <span className="block text-[0.72em] opacity-60 font-normal">as of {s.asOf}</span>
            )}
          </div>
          <div
            className="font-[family-name:var(--font-instrument)] text-[22px] max-[480px]:text-[16px] font-semibold text-[var(--etf-white)] tracking-[-0.01em]"
            style={s.color ? { color: s.color } : undefined}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatAsOfDate(input: string | null | undefined): string | null {
  if (!input) return null;
  if (input === "[ ]" || input === "TBD" || input === "Pre-Launch") return null;
  const isIso10 = /^\d{4}-\d{2}-\d{2}$/.test(input);
  const candidate = isIso10 ? `${input}T12:00:00` : input;
  const d = new Date(candidate);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/New_York",
  });
}
